import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/TreeMarking';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const TREE_MARKING_API_REDUCER_KEY = 'tree-markings';
export const TREE_MARKING_PATH = 'tree-markings';

export const treeMarkingApi = createApi({
    reducerPath: TREE_MARKING_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['TreeMarking', 'TreeMarkings'],
    endpoints: (builder) => ({
        getTreeMarkingList: builder.query<PaginationResult<Entity.TreeMarking>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ TREE_MARKING_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['TreeMarkings'],
        }),
        getTreeMarking: builder.query<Entity.TreeMarking, Entity.PrimaryTreeMarking>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = treeMarkingNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ TREE_MARKING_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['TreeMarking'],
        }),
        getTreeMarkingMultiple: builder.query<Entity.TreeMarking, Entity.PrimaryTreeMarking[]>({
            query: (ids) => {
                return ({
                    url: `/${ TREE_MARKING_PATH }/ids${Entity.PrimaryTreeMarking.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['TreeMarkings'],
        }),
        getTreeMarkingPresentation: builder.query<PaginationResult<Entity.TreeMarking>, Entity.PresentationTreeMarkingRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ TREE_MARKING_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['TreeMarkings'],
        }),
        getTreeMarkingAdvancedPresentation: builder.query<PaginationResult<Entity.TreeMarking>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ TREE_MARKING_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['TreeMarkings'],
        }),
        
        insertTreeMarking: builder.mutation<Entity.TreeMarking, Entity.CreateTreeMarking>({
            query: (toInsert) => {
                return ({
                    url: `/${ TREE_MARKING_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    treeMarkingApi.util.upsertQueryData('getTreeMarking', Entity.PrimaryTreeMarking.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['TreeMarkings'],
        }),
        insertMultipleTreeMarking: builder.mutation<Entity.TreeMarking[], Entity.CreateTreeMarking[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ TREE_MARKING_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            treeMarkingApi.util.upsertQueryData('getTreeMarking', Entity.PrimaryTreeMarking.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['TreeMarkings'],
        }),
        updateTreeMarking: builder.mutation<Entity.TreeMarking, Entity.UpdateEntityTreeMarking>({
            query: (toUpdate) => {
                return ({
                    url: `/${ TREE_MARKING_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['TreeMarkings'],
        }),
        updateMultipleTreeMarking: builder.mutation<Entity.TreeMarking[], Entity.UpdateEntityTreeMarking[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ TREE_MARKING_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['TreeMarkings'],
        }),
        deleteTreeMarking: builder.mutation<Entity.TreeMarking, Entity.TreeMarking>({
            query: (toDelete) => {
                return ({
                    url: `/${ TREE_MARKING_PATH }/${Entity.PrimaryTreeMarking.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['TreeMarkings'],
        }),
        deleteMultipleTreeMarking: builder.mutation<Entity.TreeMarking[], Entity.PrimaryTreeMarking[]>({
            query: (ids) => {
                return ({
                    url: `/${ TREE_MARKING_PATH }/batch${Entity.PrimaryTreeMarking.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['TreeMarkings'],
        }),
    }),
})

export const [treeMarkingNormalizer, treeMarkingNormalizerMiddleware] = createNormalizationMiddleware(TREE_MARKING_API_REDUCER_KEY, treeMarkingApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetTreeMarkingListQuery,
    useGetTreeMarkingMultipleQuery,
    useGetTreeMarkingPresentationQuery,
    useGetTreeMarkingQuery,
    useInsertTreeMarkingMutation,
    useInsertMultipleTreeMarkingMutation,
    useUpdateTreeMarkingMutation,
    useUpdateMultipleTreeMarkingMutation,
    useDeleteTreeMarkingMutation,
    useDeleteMultipleTreeMarkingMutation,
    useGetTreeMarkingAdvancedPresentationQuery,
    
} = treeMarkingApi
